import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox, Icon } from "semantic-ui-react";

import { DetailsCard, useCan } from "components";
import { setShowPriceInVat, useShowPriceInVat } from "hooks";
import { STATUS_IDENTIFIERS } from "models";
import { isHiddenPriceIntervention } from "modules/AppointmentDetails/components/CustomerCommunicationModal";
import "modules/AppointmentDetails/components/CustomerCommunicationModal/components/CustomerCommunicationInterventions/CustomerCommunicationInterventions.scss";
import {
  RepairOverviewInterventionItem,
  RepairOverviewInterventionPrice
} from "modules/AppointmentDetails/components/CustomerCommunicationModal/components/RepairOverviewInterventions/components/index";
import { useAppointmentLocation, useUpdateInterventionMutation } from "modules/AppointmentDetails/hooks";
import { getInterventionTotals } from "utils/common";
import { formatPrice } from "utils/formatPrice";

import type { Appointment, Intervention } from "models";

type RepairOverviewInterventionsProps = {
  appointment: Appointment;
  invalidInterventions?: boolean;
};

export const RepairOverviewInterventions = ({ appointment, invalidInterventions }: RepairOverviewInterventionsProps) => {
  const selectedLocation = useAppointmentLocation();
  const canUpdateInterventions = useCan("update", "appointments");
  const { mutate: updateIntervention } = useUpdateInterventionMutation();
  const { t } = useTranslation();
  const showVat = useShowPriceInVat();

  const fixedInterventions = appointment.interventions?.filter(intervention => intervention.mechanic_fixed);
  const notFixedInterventions = appointment.interventions?.filter(intervention => !intervention.mechanic_fixed);

  const fixedSelectedTotals = useMemo(() => {
    const eligibleInterventions =
      appointment.interventions?.filter(
        intervention => intervention.mechanic_fixed && intervention.visible_to_customer && !isHiddenPriceIntervention(intervention, selectedLocation)
      ) || [];
    return getInterventionTotals(eligibleInterventions, selectedLocation, showVat);
  }, [appointment, selectedLocation, showVat]);

  const toggleVisibleInPDF = (item: Intervention) => {
    if (!canUpdateInterventions) return;

    if (appointment.appointment_status_identifier !== STATUS_IDENTIFIERS.CanceledStatus) {
      updateIntervention({
        ...item,
        visible_to_customer: !item.visible_to_customer
      });
    }
  };

  return (
    <>
      <DetailsCard
        title={`${t("v8_interventions", "Interventions")} ${appointment.interventions?.length ? `(${appointment.interventions.length})` : ""}`}
        icon="screwdriver wrench"
        isExpanded={true}
        showExpandArrow={false}
        containerStyle={invalidInterventions ? "error" : ""}
        rightContent={
          <div className="full end">
            <p>{t("v8_show_prices_with_vat", "Show prices with VAT")}</p>
            <Checkbox toggle checked={showVat} onChange={() => setShowPriceInVat(!showVat)} />
          </div>
        }
      >
        <div className="CustomerCommunicationInterventions">
          <div className="CustomerCommunicationInterventions-container">
            {!!notFixedInterventions?.length && (
              <div className="CustomerCommunicationInterventions-section">
                <div className="CustomerCommunicationInterventions-section-group">
                  <div className="CustomerCommunicationInterventions-section-group_header">
                    <Icon className="wrench solid not-fixed-wrench rotate-270" />
                    <p>{t("v8_not_fixed", "Not Fixed")}</p>
                  </div>
                  {notFixedInterventions.map((item: Intervention) => {
                    return <RepairOverviewInterventionItem key={item.id} intervention={item} toggleVisibleInPDF={toggleVisibleInPDF} showVat={showVat} />;
                  })}
                </div>
              </div>
            )}

            {!!fixedInterventions?.length && (
              <div className="CustomerCommunicationInterventions-section">
                <div className="CustomerCommunicationInterventions-section-group">
                  <div className="CustomerCommunicationInterventions-section-group_header">
                    <div className="mechanic-fixed-status-container">
                      <Icon className="wrench green pointer" />
                      <Icon className="check circle green wrench-check-icon" />
                    </div>

                    <p>{t("v8_fixed", "Fixed")}</p>
                  </div>
                  {fixedInterventions.map((item: Intervention) => {
                    return <RepairOverviewInterventionItem key={item.id} intervention={item} toggleVisibleInPDF={toggleVisibleInPDF} showVat={showVat} />;
                  })}
                </div>
              </div>
            )}
          </div>

          <RepairOverviewInterventionPrice
            totalFixedSelectedPriceInterventions={formatPrice(fixedSelectedTotals.totalAmount)}
            showVat={showVat}
            vatGroups={fixedSelectedTotals.vatGroups}
          />
        </div>
      </DetailsCard>
    </>
  );
};
